// DOM hooks for the new-task entry flow (OP-2534): the grid moves focus from
// the quick-add row to the new task's date, then to its "+ Add" product
// button, without either control needing a ref threaded through antd's Table.

import { EDITABLE_NUMBER_CELL_ATTR } from 'components/common/EditableCell';

import {
  productionSheetNavColumn,
  STEP_NAV_COL_ATTR,
  STEP_NAV_COL_RATE,
} from './focusAdjacentStepCell';

/** Marks a task row's date cell wrapper, valued with the task id. */
export const TASK_DATE_CELL_ATTR = 'data-task-date-cell';
/** Marks a task's "+ Add" product control wrapper, valued with the task id. */
export const TASK_ADD_INPUT_ATTR = 'data-task-add-input';

/** Marks the "Add inputs" button in a new task's popover (new layout), valued with the task id. */
export const TASK_ENTRY_ADD_INPUTS_ATTR = 'data-task-entry-add-inputs';

/** Focuses the "Add inputs" button in a new task's popover, so Enter moves on. */
export const focusTaskEntryAddInputs = (taskId: string): void => {
  findByAttr(TASK_ENTRY_ADD_INPUTS_ATTR, taskId)
    ?.querySelector<HTMLButtonElement>('button')
    ?.focus();
};

const escapeId = (id: string) =>
  typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;

const findByAttr = (attr: string, taskId: string) =>
  document.querySelector<HTMLElement>(`[${attr}="${escapeId(taskId)}"]`);

/** Focuses the task's date cell, which opens its date picker. */
export const focusTaskDateCell = (taskId: string): void => {
  findByAttr(TASK_DATE_CELL_ATTR, taskId)
    ?.querySelector<HTMLElement>('[tabindex="0"]')
    ?.focus();
};

/** Marks a product's row in the category sheet, valued with the step id. */
export const TASK_STEP_ROW_ATTR = 'data-task-step-row';

// A product's first number to enter: a production step's lowest yield shown
// (an Actual one has no Low), else the rate.
const STEP_VALUE_COLUMNS = [
  productionSheetNavColumn('yieldLow'),
  productionSheetNavColumn('yield'),
  STEP_NAV_COL_RATE,
];

/**
 * Focuses a just-picked product's first number in the category sheet (its
 * rate, or a production step's yield), which opens it for typing. A
 * production task has one product, so its block is searched as a whole.
 */
export const focusSheetStepValue = (taskId: string, stepId: string): void => {
  const scope =
    document.querySelector<HTMLElement>(
      `.task-sheet [${TASK_STEP_ROW_ATTR}="${escapeId(stepId)}"]`
    ) ??
    document
      .querySelector<HTMLElement>(
        `.task-sheet [data-row-key="${escapeId(taskId)}"]`
      )
      ?.closest('tbody');
  if (!scope) return;
  for (const column of STEP_VALUE_COLUMNS) {
    const cell = scope.querySelector<HTMLElement>(
      `td[${STEP_NAV_COL_ATTR}="${column}"] [${EDITABLE_NUMBER_CELL_ATTR}]:not([aria-disabled="true"])`
    );
    if (cell) {
      cell.focus();
      return;
    }
  }
};

/** Focuses the task's "+ Add" product button, so Enter opens the picker. */
export const focusTaskAddInput = (taskId: string): void => {
  findByAttr(TASK_ADD_INPUT_ATTR, taskId)
    ?.querySelector<HTMLButtonElement>('button')
    ?.focus();
};

/**
 * True when a picker has just closed and nothing else has taken focus: it
 * dropped to the page, or it's still inside the closing popup (antd removes
 * the popup after its exit animation, focus and all). Then the picker hands
 * focus back to the cell that opened it. A click on another cell leaves
 * focus there instead.
 */
export const isFocusLeftWithPicker = (): boolean => {
  const active = document.activeElement;
  return (
    !active || active === document.body || !!active.closest('.ant-popover')
  );
};

/** Scrolls the task's row into view, only as far as needed. */
export const scrollTaskRowIntoView = (taskId: string): void => {
  document
    .querySelector<HTMLElement>(
      `.input-tasks-table tr[data-row-key="${escapeId(taskId)}"], .task-sheet [data-row-key="${escapeId(taskId)}"]`
    )
    ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
};
